"use client";

// Library
import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import { useState, useRef } from "react";
import { toast } from "react-toastify";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";

function MentorApplicationFormInner() {
    const t = useTranslations();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        motivation: "",
    });

    // reCAPTCHA v2 fallback
    const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
    const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
    const recaptchaV2Ref = useRef(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleRecaptchaV2Change = (token) => {
        setRecaptchaV2Token(token);
    };

    const submitWithRecaptcha = async (data, recaptchaToken, recaptchaVersion) => {
        const response = await fetch("/api/mentor/apply", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...data,
                recaptchaToken,
                recaptchaVersion,
            }),
        });

        const responseData = await response.json();

        if (response.ok) {
            return { success: true };
        } else {
            return { success: false, error: responseData.error, message: responseData.message };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (!executeRecaptcha) {
                toast.error(t("signup_recaptcha_error"));
                setLoading(false);
                return;
            }

            const recaptchaToken = await executeRecaptcha("mentor_apply");
            const result = await submitWithRecaptcha(formData, recaptchaToken, "v3");

            if (result.success) {
                toast.success(t("mentorForm_success"));
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    motivation: "",
                });
                setShowRecaptchaV2(false);
                setRecaptchaV2Token(null);
            } else if (result.error === "RECAPTCHA_LOW_SCORE") {
                // Show v2 challenge
                setShowRecaptchaV2(true);
                toast.info(t("signup_recaptcha_verify"));
            } else {
                // Les messages de la route sont en français en dur : ils servent
                // au debug, jamais à l'affichage (cette page sert aussi /en/join-mentors).
                console.error("Mentor form error:", result.error, result.message);
                toast.error(t("mentorForm_error"));
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error(t("mentorForm_error"));
        } finally {
            setLoading(false);
        }
    };

    const handleRecaptchaV2Submit = async () => {
        if (!recaptchaV2Token) return;

        // Ce bouton n'est pas un submit : la validation HTML5 required ne joue pas.
        if (
            !formData.firstName ||
            !formData.lastName ||
            !formData.email ||
            !formData.motivation
        ) {
            toast.error(t("mentorForm_error"));
            return;
        }

        setLoading(true);
        try {
            const result = await submitWithRecaptcha(formData, recaptchaV2Token, "v2");

            if (result.success) {
                toast.success(t("mentorForm_success"));
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    motivation: "",
                });
                setShowRecaptchaV2(false);
                setRecaptchaV2Token(null);
            } else {
                console.error("Mentor form error:", result.error, result.message);
                toast.error(t("mentorForm_error"));
                recaptchaV2Ref.current?.reset();
                setRecaptchaV2Token(null);
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error(t("mentorForm_error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="mentor-application">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
            >
                <h2 className="md:text-5xl text-4xl dark:text-white font-bold mb-4 text-center">
                    {t("mentorForm_title")}
                </h2>
                <p className="text-center dark:text-lightBlue text-gray-600 mb-10 max-w-2xl mx-auto">
                    {t("mentorForm_subtitle")}
                </p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="max-w-3xl mx-auto bg-white dark:bg-box rounded-2xl shadow-xl border dark:border-box-outline p-8 md:p-10"
                >
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Prénom et Nom */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label
                                    htmlFor="firstName"
                                    className="block text-sm font-medium dark:text-white text-gray-700 mb-2"
                                >
                                    {t("mentorForm_firstName")}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-box-outline dark:bg-background dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-hidden"
                                    placeholder={t(
                                        "mentorForm_firstName_placeholder",
                                    )}
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="block text-sm font-medium dark:text-white text-gray-700 mb-2"
                                >
                                    {t("mentorForm_lastName")}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-box-outline dark:bg-background dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-hidden"
                                    placeholder={t(
                                        "mentorForm_lastName_placeholder",
                                    )}
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-sm font-medium dark:text-white text-gray-700 mb-2"
                            >
                                {t("mentorForm_email")}
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-box-outline dark:bg-background dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-hidden"
                                placeholder={t("mentorForm_email_placeholder")}
                            />
                        </div>

                        {/* Motivations */}
                        <div>
                            <label
                                htmlFor="motivation"
                                className="block text-sm font-medium dark:text-white text-gray-700 mb-2"
                            >
                                {t("mentorForm_motivation")}
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <textarea
                                id="motivation"
                                name="motivation"
                                value={formData.motivation}
                                onChange={handleChange}
                                required
                                rows={6}
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-box-outline dark:bg-background dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-hidden resize-none"
                                placeholder={t(
                                    "mentorForm_motivation_placeholder",
                                )}
                            />
                        </div>

                        {/* Une seule action à la fois : le bouton principal
                            disparaît dès que le repli v2 s'affiche, sinon deux
                            boutons identiques cohabitent et celui du haut rejoue
                            la v3 qu'on vient d'échouer. */}
                        {!showRecaptchaV2 && (
                            <motion.button
                                type="submit"
                                disabled={loading}
                                whileHover={{ scale: loading ? 1 : 1.02 }}
                                whileTap={{ scale: loading ? 1 : 0.98 }}
                                className="w-full py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg shadow-lg shadow-primary/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        {t("mentorForm_sending")}
                                    </>
                                ) : (
                                    <>
                                        {t("mentorForm_submit")}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={2}
                                            stroke="currentColor"
                                            className="w-5 h-5"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                                            />
                                        </svg>
                                    </>
                                )}
                            </motion.button>
                        )}

                        {/* reCAPTCHA v2 fallback */}
                        {showRecaptchaV2 && (
                            <div className="flex flex-col items-center gap-4">
                                <p className="text-sm text-muted-foreground text-center">
                                    {t("signup_recaptcha_verify")}
                                </p>
                                <ReCAPTCHA
                                    ref={recaptchaV2Ref}
                                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY || ""}
                                    onChange={handleRecaptchaV2Change}
                                />
                                {recaptchaV2Token && (
                                    <motion.button
                                        type="button"
                                        onClick={handleRecaptchaV2Submit}
                                        disabled={loading}
                                        whileHover={{ scale: loading ? 1 : 1.02 }}
                                        whileTap={{ scale: loading ? 1 : 0.98 }}
                                        className="w-full py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg shadow-lg shadow-primary/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <svg
                                                    className="animate-spin h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                {t("mentorForm_sending")}
                                            </>
                                        ) : (
                                            <>
                                                {t("mentorForm_submit")}
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={2}
                                                    stroke="currentColor"
                                                    className="w-5 h-5"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                                                    />
                                                </svg>
                                            </>
                                        )}
                                    </motion.button>
                                )}
                            </div>
                        )}

                        <p className="text-sm text-center dark:text-lightBlue text-gray-500 mt-4">
                            {t("mentorForm_privacy")}
                        </p>
                    </form>
                </motion.div>
            </motion.div>
        </section>
    );
}

export default function MentorApplicationForm() {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY}
            scriptProps={{
                async: true,
                defer: true,
            }}
        >
            <MentorApplicationFormInner />
        </GoogleReCaptchaProvider>
    );
}
